"use client";

import ContactCard from "@components/ContactCard/ContactCard";
import { linkTracker } from "@components/Link/linkTracker";
import { BlockSize } from "@components/cFContentArea";
import { documentToHtmlString } from "@contentful/rich-text-html-renderer";
import { getImageMetaData } from "@utils/imageHelpers/metadataImageHelper";
import { getPortraitImage } from "@utils/imageHelpers/portraitImageHelper";
import { getInternalOrExternalIcon } from "@utils/linkIconHelper";
import { shouldRemoveBorder } from "@utils/teaserHelper/teaserHelper";
import { replaceImageVaultBasePath } from "@utils/urlResolver";
import clsx from "clsx";
import { useRef } from "react";

import { Context } from "types/context/Context";

import contactBlockStyles from "./contactBlock.module.css";
import { ContactBlockType_contactBlock } from "./queries/contentfulTypes/ContactBlockType";

interface ContactBlockData extends ContactBlockType_contactBlock {
  contactBlockUrl: string;
}

interface IContactBlockProps {
  blockData?: ContactBlockData;
  uniqueKey?: string;
  context?: Context;
}

export const ContactBlock = ({ blockData: contactBlock, context }: IContactBlockProps) => {
  const url = contactBlock?.contactBlockUrl;
  const linkType = getInternalOrExternalIcon(
    contactBlock?.externalLink,
    contactBlock?.internalLink,
    null,
  );
  const variant = contactBlock?.linkVariant ? "button" : "link";
  const linkRef = useRef(null);
  const addressLinkRef = useRef(null);

  const biography = documentToHtmlString(contactBlock?.biography?.json);
  const imageData = contactBlock?.ivImage?.[0];
  const image = getPortraitImage(imageData);
  const metadata = getImageMetaData(imageData);

  const contextType = context?.columns || 1;

  return (
    <div
      data-testid="contact-block"
      data-contentful-entry-id={contactBlock.sys.id}
      data-contentful-field-id="name"
      className={clsx(contactBlockStyles.contactBlock, {
        [contactBlockStyles.multiColContext]: contextType > 1,
        "full-width":
          context?.sizes?.desktop === BlockSize.Full || context?.sizes?.notebook === BlockSize.Full,
      })}
    >
      <ContactCard
        personalName={contactBlock?.personalName}
        headingVariant={context?.heading?.parentLevel}
        imageUrl={replaceImageVaultBasePath(image?.url)}
        imageAlt={metadata?.altText}
        email={contactBlock?.email}
        emailTitleTag={contactBlock?.emailTitleTag}
        emailOnClick={() =>
          linkTracker(contactBlock?.email, contactBlock?.email, { linkType: "contact" })
        }
        phone={contactBlock?.phone}
        phoneLabel={contactBlock?.phoneLabel}
        phoneTitleTag={contactBlock?.phoneTitleTag}
        phoneOnClick={() =>
          linkTracker(contactBlock?.phone, contactBlock?.phone, { linkType: "contact" })
        }
        phoneTwo={contactBlock?.phoneTwo}
        phoneTwoLabel={contactBlock?.phoneTwoLabel}
        phoneTwoTitleTag={contactBlock?.phoneTwoTitleTag}
        phoneTwoOnClick={() =>
          linkTracker(contactBlock?.phoneTwo, contactBlock?.phoneTwo, { linkType: "contact" })
        }
        jobTitle={contactBlock?.jobTitel}
        extraInfo={contactBlock?.extraInfo}
        address={contactBlock?.address}
        addressType={contactBlock?.addressType}
        addressLink={contactBlock?.addressLink}
        addressRef={addressLinkRef}
        box={contactBlock?.box}
        zipCode={contactBlock?.zipCode}
        faxLabel={contactBlock?.faxLabel}
        fax={contactBlock?.fax}
        faxTitleTag={contactBlock?.faxTitleTag}
        state={contactBlock?.state}
        biography={biography && <div dangerouslySetInnerHTML={{ __html: biography }}></div>}
        linkUrl={url}
        linkType={linkType}
        linkText={contactBlock?.linkText}
        linkRef={linkRef}
        variant={variant}
        data-blockname="kontaktkort"
        removeBorder={shouldRemoveBorder(context)}
        context={context}
      />
    </div>
  );
};
